import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { defaultYear } from '../../common/constant';
import { constructMaskId } from '../../common/constructMaskId';
import { RootState } from '../../rootReducer';

interface DownloadOption {
  // Temporal range.
  yearMax: number;
  yearMin: number;

  // Spatial domain.
  latMax: number;
  latMin: number;
  lonMax: number;
  lonMin: number;
  maskLevel: number;
  maskValues: {
    [prop: number]: number;
  };
  maskResolutionIds: {
    [prop: number]: number;
  };

  // Period average.
  periodAverageEnabled: boolean;

  // Spatial resolution.
  interpolation: 'linear' | 'surface';
  nx?: number;
  ny?: number;
  spatialResolution: 'raw' | 'userdefined';
  useMaskResolutionChecked: boolean;

  // File format
  fileFormat?: 'netcdf' | 'fullbig' | 'fulllittle' | 'htbig' | 'htlittle';
  extension: string;
  meridionalSequence: '1' | '2'; // 'north2south' | 'south2north'

  // Compression
  compression: 'yes' | 'no';
}

const initialState: DownloadOption = {
  // Temporal range.
  yearMin: defaultYear,
  yearMax: defaultYear,

  // Spatial domain.
  latMax: 90,
  latMin: -90,
  lonMax: 180,
  lonMin: -180,
  maskLevel: -1,
  maskValues: {},
  maskResolutionIds: {},

  // Period average.
  periodAverageEnabled: false,

  // Spatial resolution.
  interpolation: 'linear',
  // nx
  // ny
  spatialResolution: 'raw',
  useMaskResolutionChecked: true,

  // File format
  // fileFormat
  extension: '.bin',
  meridionalSequence: '1',

  // Compression
  compression: 'yes',
};

const downloadOptionSlice = createSlice({
  name: 'downloadOption',
  initialState,
  reducers: {
    // Temporal range.
    updateYearMax(state, action: PayloadAction<{yearMax: DownloadOption['yearMax']}>) {
      const { yearMax } = action.payload;
      state.yearMax = yearMax;
    },
    updateYearMin(state, action: PayloadAction<{yearMin: DownloadOption['yearMin']}>) {
      const { yearMin } = action.payload;
      state.yearMin = yearMin;
    },

    // Spatial domain.
    updateLatMax(state, action: PayloadAction<{latMax: DownloadOption['latMax']}>) {
      const { latMax } = action.payload;
      state.latMax = latMax;
    },
    updateLatMin(state, action: PayloadAction<{latMin: DownloadOption['latMin']}>) {
      const { latMin } = action.payload;
      state.latMin = latMin;
    },
    updateLonMax(state, action: PayloadAction<{lonMax: DownloadOption['lonMax']}>) {
      const { lonMax } = action.payload;
      state.lonMax = lonMax;
    },
    updateLonMin(state, action: PayloadAction<{lonMin: DownloadOption['lonMin']}>) {
      const { lonMin } = action.payload;
      state.lonMin = lonMin;
    },
    updateMaskLevel(state, action: PayloadAction<{maskLevel: DownloadOption['maskLevel']}>) {
      const { maskLevel } = action.payload;
      state.maskLevel = maskLevel;
    },
    updateMaskValues(state, action: PayloadAction<{level: number; value: number}>) {
      const { level, value } = action.payload;
      state.maskValues[level] = value;
    },
    updateMaskResolutionIds(state, action: PayloadAction<{level: number; resolutionId: number}>) {
      const { level, resolutionId } = action.payload;
      state.maskResolutionIds[level] = resolutionId;
    },

    // Period average.
    updatePeriodAverageEnabled(state, action: PayloadAction<{periodAverageEnabled: DownloadOption['periodAverageEnabled']}>) {
      const { periodAverageEnabled } = action.payload;
      state.periodAverageEnabled = periodAverageEnabled;
    },

    // Spatial resolution.
    updateInterpolation(state, action: PayloadAction<{interpolation: DownloadOption['interpolation']}>) {
      const { interpolation } = action.payload;
      state.interpolation = interpolation;
    },
    updateNx(state, action: PayloadAction<{nx: DownloadOption['nx']}>) {
      const { nx } = action.payload;
      state.nx = nx;
    },
    updateNy(state, action: PayloadAction<{ny: DownloadOption['ny']}>) {
      const { ny } = action.payload;
      state.ny = ny;
    },
    updateSpatialResolution(state, action: PayloadAction<{spatialResolution: DownloadOption['spatialResolution']}>) {
      const { spatialResolution } = action.payload;
      state.spatialResolution = spatialResolution;
    },
    updateUseMaskResolutionChecked(state, action: PayloadAction<{useMaskResolutionChecked: DownloadOption['useMaskResolutionChecked']}>) {
      const { useMaskResolutionChecked } = action.payload;
      state.useMaskResolutionChecked = useMaskResolutionChecked;
    },

    // File format
    updateFileFormat(state, action: PayloadAction<{fileFormat: DownloadOption['fileFormat']}>) {
      const { fileFormat } = action.payload;
      state.fileFormat = fileFormat;
    },
    updateExtension(state, action: PayloadAction<{extension: DownloadOption['extension']}>) {
      const { extension } = action.payload;
      state.extension = extension;
    },
    updateMeridionalSequence(state, action: PayloadAction<{meridionalSequence: DownloadOption['meridionalSequence']}>) {
      const { meridionalSequence } = action.payload;
      state.meridionalSequence = meridionalSequence;
    },

    // Compression
    updateCompression(state, action: PayloadAction<{compression: DownloadOption['compression']}>) {
      const { compression } = action.payload;
      state.compression = compression;
    },
  },
});

export const {
  // Temporal range.
  updateYearMax,
  updateYearMin,

  // Spatial domain.
  updateLatMax,
  updateLatMin,
  updateLonMax,
  updateLonMin,
  updateMaskLevel,
  updateMaskValues,
  updateMaskResolutionIds,

  // Period average.
  updatePeriodAverageEnabled,

  // Spatial resolution.
  updateInterpolation,
  updateNx,
  updateNy,
  updateSpatialResolution,
  updateUseMaskResolutionChecked,

  // File format
  updateFileFormat,
  updateExtension,
  updateMeridionalSequence,

  // Compression
  updateCompression,
} = downloadOptionSlice.actions;

export default downloadOptionSlice.reducer;

// Temporal range.
export const selectYearMax = (state: RootState) => {
  const { yearMax } = state.downloadOption;
  return yearMax;
};

export const selectYearMin = (state: RootState) => {
  const { yearMin } = state.downloadOption;
  return yearMin;
};

// Spatial domain.
export const selectLatMax = (state: RootState) => {
  const { latMax } = state.downloadOption;
  return latMax;
};

export const selectLatMin = (state: RootState) => {
  const { latMin } = state.downloadOption;
  return latMin;
};

export const selectLonMax = (state: RootState) => {
  const { lonMax } = state.downloadOption;
  return lonMax;
};

export const selectLonMin = (state: RootState) => {
  const { lonMin } = state.downloadOption;
  return lonMin;
};

export const selectMaskLevel = (state: RootState) => {
  const { maskLevel } = state.downloadOption;
  return maskLevel;
};

export const selectMaskValues = (state: RootState) => {
  const { maskValues } = state.downloadOption;
  return maskValues;
};

export const selectMaskResolutionIds = (state: RootState) => {
  const { maskResolutionIds } = state.downloadOption;
  return maskResolutionIds;
};

// Period average.
export const selectPeriodAverageEnabled = (state: RootState) => {
  const { periodAverageEnabled } = state.downloadOption;
  return periodAverageEnabled;
};

// Spatial resolution.
export const selectInterpolation = (state: RootState) => {
  const { interpolation } = state.downloadOption;
  return interpolation;
};

export const selectNx = (state: RootState) => {
  const { nx } = state.downloadOption;
  return nx;
};

export const selectNy = (state: RootState) => {
  const { ny } = state.downloadOption;
  return ny;
};

export const selectSpatialResolution = (state: RootState) => {
  const { spatialResolution } = state.downloadOption;
  return spatialResolution;
};

export const selectUseMaskResolutionChecked = (state: RootState) => {
  const { useMaskResolutionChecked } = state.downloadOption;
  return useMaskResolutionChecked;
};

// File format
export const selectFileFormat = (state: RootState) => {
  const { fileFormat } = state.downloadOption;
  return fileFormat;
};

export const selectExtension = (state: RootState) => {
  const { extension } = state.downloadOption;
  return extension;
};

export const selectMeridionalSequence = (state: RootState) => {
  const { meridionalSequence } = state.downloadOption;
  return meridionalSequence;
};

// Compression
export const selectCompression = (state: RootState) => {
  const { compression } = state.downloadOption;
  return compression;
};

// PostData
export const selectPostData = (state: RootState) => {
  const {
    yearMax,
    yearMin,
    latMax,
    latMin,
    lonMax,
    lonMin,
    maskLevel,
    maskValues,
    maskResolutionIds,
    periodAverageEnabled,
    interpolation,
    nx,
    ny,
    spatialResolution,
    useMaskResolutionChecked,
    fileFormat,
    extension,
    meridionalSequence,
    compression,
  } = state.downloadOption;

  const mask = maskLevel > -1;

  const ret: {[prop: string]: unknown} = {
    cmprs: compression,
    extension,
    format: fileFormat,
    interp: interpolation,
    latmaxout: latMax,
    latminout: latMin,
    lonmaxout: lonMax,
    lonminout: lonMin,
    mask,
    maskid: mask ? constructMaskId(maskLevel, maskValues[maskLevel], maskResolutionIds[maskLevel]) : null,
    nxout: nx?.toString() ?? null,
    nyout: ny?.toString() ?? null,
    optns: meridionalSequence,
    'period-average': periodAverageEnabled,
    sreso: spatialResolution,
    useMaskResolution: useMaskResolutionChecked,
    yearrange: [yearMin, yearMax],
  };

  return ret;
};
